// =============================================================================
// SimSense Platform — App Root
// =============================================================================

import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Shell } from "./layout/Shell";
import { Home } from "./pages/Home";
import { SessionOverview } from "./pages/SessionOverview";
import { LapComparison } from "./pages/LapComparison";
import { CornerAnalysis } from "./pages/CornerAnalysis";
import { Coaching } from "./pages/Coaching";
import { RunAverages } from "./pages/RunAverages";
import { Leaderboard } from "./pages/Leaderboard";
import { Profile } from "./pages/Profile";
import { Friends } from "./pages/Friends";
import { Support } from "./pages/Support";
import { Tracks } from "./pages/Tracks";
import { Cars } from "./pages/Cars";
import { Challenges } from "./pages/Challenges";
import { Teams } from "./pages/Teams";
import { TeamDetail } from "./pages/TeamDetail";
import { TeamJoin } from "./pages/TeamJoin";
import { AdminDashboard } from "./pages/AdminDashboard";
import { Settings } from "./pages/Settings";
import { Login } from "./pages/Login";
import { SignUp } from "./pages/SignUp";
import { DesktopLogin } from "./pages/DesktopLogin";
import { IracingLinkGate } from "./pages/IracingLinkGate";
import { Landing } from "./pages/Landing";
import { Store } from "./pages/Store";
import { TermsOfService } from "./pages/legal/TermsOfService";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";
import { CreatorAgreement } from "./pages/legal/CreatorAgreement";
import { AcceptableUse } from "./pages/legal/AcceptableUse";
import { MarketplaceDiscovery } from "./pages/Marketplace/MarketplaceDiscovery";
import { SetupDetailPage } from "./pages/Marketplace/SetupDetailPage";
import { ShopProfilePage } from "./pages/Marketplace/ShopProfilePage";
import { CreatorDashboardPage } from "./pages/Marketplace/CreatorDashboardPage";
import { BecomeSellerPage } from "./pages/Marketplace/BecomeSellerPage";
import { CoachDashboard } from "./pages/CoachDashboard";
import { CoachDiscovery } from "./pages/CoachDiscovery";
import { CoachProfile } from "./pages/CoachProfile";
import { useDesktopStore } from "./state/desktopStore";
import { useAuth } from "./hooks/useAuth";
import { useTauri } from "./hooks/useTauri";
import { markDesktopAuthComplete } from "./utils/desktopAuth";
import { remoteLog } from "./utils/logger";

/** Wrapper that redirects unauthenticated users to login and
 *  unlinked users to the iRacing link gate. */
function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isIracingLinked, isAdmin } = useAuth();
  const isTauri = useTauri();
  if (!isAuthenticated) {
    return isTauri ? <DesktopLogin /> : <Navigate to="/login" replace />;
  }
  if (!isIracingLinked && !isAdmin) {
    return <IracingLinkGate />;
  }
  return children;
}

/** Feature-gated route: only accessible to users whose plan matches one of allowedPlans. */
function GatedRoute({ children, allowedPlans }: { children: React.ReactElement; allowedPlans: string[] }) {
  const { user } = useAuth();
  if (!user || !allowedPlans.includes(user.plan)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// OT-211: Build info for deploy integrity verification
declare const __BUILD_TIME__: string;
declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;

(window as any).__OSC_BUILD_TIME__ = typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "dev";
(window as any).__OSC_VERSION__ = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.1.0";
(window as any).__OSC_COMMIT__ = typeof __COMMIT_SHA__ !== "undefined" ? __COMMIT_SHA__ : "unknown";

function RootPage() {
  const { isAuthenticated, isIracingLinked, isAdmin } = useAuth();
  const isTauri = useTauri();
  const [desktopOutcome, setDesktopOutcome] = useState<"idle" | "success" | "error">("idle");

  // Handle Google OAuth desktop token after the browser redirect returns to /.
  useEffect(() => {
    const token = sessionStorage.getItem("ss_desktop_token");
    if (isAuthenticated && token) {
      void markDesktopAuthComplete(token).then((completed) => {
        if (completed) {
          sessionStorage.removeItem("ss_desktop_token");
          setDesktopOutcome("success");
          return;
        }
        setDesktopOutcome("error");
      });
    }
  }, [isAuthenticated]);

  if (desktopOutcome !== "idle") {
    const isSuccess = desktopOutcome === "success";
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", flex: 1, padding: "24px",
        background: "radial-gradient(circle at top right, #1e293b, #0a0e1a)",
      }}>
        <div style={{
          background: "var(--card)", border: "1px solid var(--border)",
          borderRadius: "12px", padding: "40px", maxWidth: "440px",
          width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", textAlign: "center",
        }}>
          <h1 style={{ fontSize: "24px", fontWeight: 800, color: isSuccess ? "#22c55e" : "#f87171", marginBottom: "12px" }}>
            {isSuccess ? "Login Successful" : "Desktop Sync Failed"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: "15px" }}>
            {isSuccess
              ? "You can close this tab and return to the SimSense desktop app."
              : "Web sign-in succeeded, but the desktop app did not receive the session. Return to the desktop app and try again."}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Desktop users should go straight to login, not the marketing landing page
    return isTauri ? <DesktopLogin /> : <Landing />;
  }
  if (!isIracingLinked && !isAdmin) {
    return <IracingLinkGate />;
  }
  return <Home />;
}

function App() {
  const initializeDesktop = useDesktopStore(s => s.initialize);

  useEffect(() => {
    void remoteLog("App mounting - initializing desktop store", "info", { 
      buildTime: (window as any).__OSC_BUILD_TIME__,
      version: (window as any).__OSC_VERSION__,
      commit: (window as any).__OSC_COMMIT__
    });
    initializeDesktop();
  }, [initializeDesktop]);

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<RootPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/pricing" element={<Store />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/creator-agreement" element={<CreatorAgreement />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/session/:id" element={<ProtectedRoute><SessionOverview /></ProtectedRoute>} />
        <Route path="/session/:id/compare" element={<ProtectedRoute><LapComparison /></ProtectedRoute>} />
        <Route path="/session/:id/corners" element={<ProtectedRoute><CornerAnalysis /></ProtectedRoute>} />
        <Route path="/session/:id/coaching/:stintIndex" element={<ProtectedRoute><Coaching /></ProtectedRoute>} />
        <Route path="/session/:id/run-averages" element={<ProtectedRoute><RunAverages /></ProtectedRoute>} />
        <Route path="/marketplace" element={<ProtectedRoute><GatedRoute allowedPlans={["admin", "beta_2"]}><MarketplaceDiscovery /></GatedRoute></ProtectedRoute>} />
        <Route path="/marketplace/setup/:id" element={<ProtectedRoute><GatedRoute allowedPlans={["admin", "beta_2"]}><SetupDetailPage /></GatedRoute></ProtectedRoute>} />
        <Route path="/marketplace/shop/:slug" element={<ProtectedRoute><GatedRoute allowedPlans={["admin", "beta_2"]}><ShopProfilePage /></GatedRoute></ProtectedRoute>} />
        <Route path="/marketplace/dashboard" element={<ProtectedRoute><GatedRoute allowedPlans={["admin", "beta_2"]}><CreatorDashboardPage /></GatedRoute></ProtectedRoute>} />
        <Route path="/marketplace/sell" element={<ProtectedRoute><GatedRoute allowedPlans={["admin", "beta_2"]}><BecomeSellerPage /></GatedRoute></ProtectedRoute>} />
        <Route path="/setup" element={<Navigate to="/marketplace" replace />} />
        <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
        <Route path="/tracks" element={<ProtectedRoute><Tracks /></ProtectedRoute>} />
        <Route path="/cars" element={<ProtectedRoute><Cars /></ProtectedRoute>} />
        <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
        <Route path="/profile/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
        <Route path="/coach" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
        <Route path="/coaches" element={<ProtectedRoute><CoachDiscovery /></ProtectedRoute>} />
        <Route path="/coach/:slug" element={<ProtectedRoute><CoachProfile /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
        <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
        <Route path="/teams/:id" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />
        <Route path="/teams/join/:token" element={<ProtectedRoute><TeamJoin /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      </Routes>
    </Shell>
  );
}

export default App;
